import parseColors from './parseColors';
import originalThemes from './definitions';
import type {
  Theme,
  ThemeCollection,
  ThemeDefinition,
  AllColorTokens,
  RadiusToken,
  SpacingToken,
  BorderWidthToken,
} from './types';

import type { FontToken, FontProperties } from '../typography/types';
import { getDimensionToken } from '../sizes/getDimensionToken';
import type { PrimitiveColors } from '../colors/colors';
import { colors } from '../colors/colors';

/**
 * Theme type definitions for external use
 *
 * Provides all theme-related types including theme structure,
 * color tokens (primitive and semantic), and helper types for
 * working with the design system's theming API.
 */
export type {
  Theme,
  ThemeCollection,
  ThemeDefinition,
  ThemeId,
  ThemeColors,
  ParsedThemeColors,
  AllColorTokens,
  SemanticColorToken,
  ColorToken,
  ColorReference,
  PrimitiveColorName,
  ColorScaleName,
  SimpleColorName,
  BackgroundColorTokens,
  ForegroundColorTokens,
  BorderColorTokens,
  CardColorTokens,
  ButtonColorTokens,
  TagColorTokens,
} from './types';

/**
 * Typography types for theme configuration
 * Re-exported from typography module for convenience
 */
export type { TypographyConfig, FontToken } from '../typography/types';

// Export default theme constants
export { DEFAULT_THEME, DEFAULT_THEME_DARK } from './types';

const parseTheme = (theme: ThemeDefinition): Theme => {
  const parsedColors = parseColors(theme.colors);

  const getColor = (colorToken: AllColorTokens): string => {
    const color = parsedColors[colorToken];
    if (typeof color === 'string') {
      return color;
    }

    // Fallback: resolve primitive color tokens like 'goldenGlow.300' or simple names like 'white'
    if (typeof colorToken === 'string') {
      if (colorToken.includes('.')) {
        const [colorName, shade] = colorToken.split('.') as [keyof PrimitiveColors, string];
        const colorScale = colors[colorName as keyof PrimitiveColors];
        if (typeof colorScale === 'object' && colorScale !== null && shade in colorScale) {
          const hexValue = (colorScale as Record<string, string>)[shade];
          if (hexValue) {
            return hexValue;
          }
        }
      } else {
        const primitive = colors[colorToken as keyof PrimitiveColors];
        if (typeof primitive === 'string') {
          return primitive;
        }
      }
    }

    return 'black';
  };

  const getFont = (fontToken: FontToken): FontProperties => {
    if (fontToken.includes('.')) {
      const [fontCategory, fontSize] = fontToken.split('.') as [keyof typeof theme.typography, keyof FontProperties];
      const fontCategoryData = theme.typography[fontCategory];
      if (fontCategoryData && fontCategoryData[fontSize as keyof typeof fontCategoryData]) {
        return fontCategoryData[fontSize as keyof typeof fontCategoryData] as FontProperties;
      }
    } else {
      // If just the size (like "M_SemiBold"), get from text property
      const textFont = theme.typography.body[fontToken as keyof typeof theme.typography.body];
      if (textFont) {
        return textFont as FontProperties;
      }
    }

    // Fallback to text.md if token not found
    return theme.typography.body.M_Regular as FontProperties;
  };

  function getSpacing(spacingToken: SpacingToken): string;
  function getSpacing(spacingToken: SpacingToken, withUnit: false): number;
  function getSpacing(spacingToken: SpacingToken, withUnit?: boolean): string | number {
    if (withUnit === false) return getDimensionToken('spacing', spacingToken, theme.sizes, false);
    return getDimensionToken('spacing', spacingToken, theme.sizes);
  }

  function getRadius(radiusToken: RadiusToken): string;
  function getRadius(radiusToken: RadiusToken, withUnit: false): number;
  function getRadius(radiusToken: RadiusToken, withUnit?: boolean): string | number {
    if (withUnit === false) return getDimensionToken('radius', radiusToken, theme.sizes, false);
    return getDimensionToken('radius', radiusToken, theme.sizes);
  }

  function getLayout<T extends string>(layoutProperty: string, token: T): string;
  function getLayout<T extends string>(layoutProperty: string, token: T, withUnit: false): number;
  function getLayout<T extends string>(layoutProperty: string, token: T, withUnit?: boolean): string | number {
    if (withUnit === false) return getDimensionToken('layout', `${layoutProperty}.${token}`, theme.sizes, false);
    return getDimensionToken('layout', `${layoutProperty}.${token}`, theme.sizes);
  }

  function getBorderWidth(borderToken: BorderWidthToken): string;
  function getBorderWidth(borderToken: BorderWidthToken, withUnit: false): number;
  function getBorderWidth(borderToken: BorderWidthToken, withUnit?: boolean): string | number {
    const borderValue = (theme.sizes.border as Record<BorderWidthToken, number>)[borderToken] ?? 0;
    if (withUnit === false) return borderValue;
    if (borderValue === 0) return '0';
    return `${borderValue}px`;
  }

  return {
    ...theme,
    colors: parsedColors,
    getColor,
    getFont,
    getRadius,
    getSpacing,
    getLayout,
    getBorderWidth,
  } as Theme;
};

const themes: ThemeCollection = Object.entries(originalThemes).reduce((acc: ThemeCollection, [key, theme]) => {
  acc[key] = parseTheme(theme);
  return acc;
}, {});

export { originalThemes, themes, parseTheme };
